import randomUUID from '@/utils/uuid'
import { workflowApiHelpers } from '@/api/workflowApiHelpers'
import cardHelpers from '@/helpers/cardHelpers'

export default {
  isValidShape (jsonData) {
    return jsonData && jsonData.id && jsonData.type && jsonData.description
  },
  async checkClipboardValidity () {
    try {
      const clipboardItems = await navigator.clipboard.read()
      let foundCustomJson = false
      for (const clipboardItem of clipboardItems) {
        if (clipboardItem.types.includes('text/html')) {
          const blob = await clipboardItem.getType('text/html')
          const htmlString = await blob.text()

          const parser = new DOMParser()
          const doc = parser.parseFromString(htmlString, 'text/html')

          // Check for multi-selection clipboard data
          const multiEventsElement = doc.querySelector('[data-events-json]')
          if (multiEventsElement) {
            const customJsonString = multiEventsElement.getAttribute('data-events-json')
            if (customJsonString) {
              try {
                const jsonData = JSON.parse(customJsonString)
                if (jsonData.isMultiSelection && Array.isArray(jsonData.events) && jsonData.events.length > 0) {
                  // Validate that all events in the array are valid shapes
                  const allValid = jsonData.events.every(eventData => this.isValidShape(eventData))
                  if (allValid) {
                    foundCustomJson = true
                    break
                  } else {
                    console.warn('Clipboard contains multi-selection data but some events are invalid')
                  }
                }
              } catch (e) {
                console.warn('Failed to parse multi-selection clipboard data:', e)
              }
            }
          }

          // Check for single event clipboard data
          const customElement = doc.querySelector('[data-event-json]')
          if (customElement) {
            const customJsonString = customElement.getAttribute('data-event-json')
            if (customJsonString) {
              try {
                const jsonData = JSON.parse(customJsonString)
                if (this.isValidShape(jsonData)) {
                  foundCustomJson = true
                  break
                }
              } catch (e) {
                console.warn('Failed to parse single event clipboard data:', e)
              }
            }
          }
        }
      }
      return foundCustomJson
    } catch (e) {
      console.warn('Failed to check clipboard validity:', e)
      return false
    }
  },
  prepareAddShape (jsonData, selectedEvent, svgGraphData, svgLanesData, workflow, cardTypes, isMultiShape = false) {
    return this.copyEventFields(jsonData, selectedEvent, svgGraphData, svgLanesData, workflow, cardTypes, isMultiShape)
  },
  copyEventFields (jsonData, selectedEvent, svgGraphData, svgLanesData, workflow, cardTypes, isMultiShape = false) {
    const shape = {
      id: this.getIdForNewShape(jsonData, svgGraphData, workflow),
      description: jsonData.description,
      laneId: this.getLaneIdForNewShape(jsonData, svgLanesData, workflow),
      groupId: undefined,
      type: jsonData.type,
      conditionLabels: this.getConditionLabels(jsonData, selectedEvent, isMultiShape),
      cards: this.getCards(jsonData, svgGraphData, workflow, cardTypes),
      subprocess: jsonData.subprocess, // check if we need to remove subprocess if the workflow doesn't exist
      color: jsonData.color,
      dataModels: jsonData.dataModels
    }
    shape.offset = selectedEvent ? undefined : workflowApiHelpers.findPreferredOffset(shape, svgGraphData, svgLanesData)
    return shape
  },
  getIdForNewShape (jsonData, svgGraphData, workflow) {
    let id
    if (!svgGraphData[jsonData.id] && workflow.id === jsonData.workflowId) {
      // if the shape does not exist and the workflow is the same
      // then keep the id because it is a CUT-paste operation
      id = jsonData.id // keep the id
    } else {
      // if the shape shape exists and or we have switched workflow
      // then generate a new id because it is likely a COPY-paste operation
      id = randomUUID()
    }
    return id
  },
  getLaneIdForNewShape (jsonData, svgLanesData, workflow) {
    if (svgLanesData[jsonData.laneId]) {
      return jsonData.laneId
    }
    // find lane by name
    const lanes = Object.values(svgLanesData)
    const lane = lanes.find(lane => lane.name === jsonData.laneName)
    if (lane) {
      return lane.id
    }
    // find the last lane in svgLanesData
    let maxIndex = 0
    for (const laneId in svgLanesData) {
      if (svgLanesData[laneId].index > maxIndex) {
        maxIndex = svgLanesData[laneId].index
      }
    }
    return Object.keys(svgLanesData).find((laneId) => svgLanesData[laneId].index === maxIndex)
  },
  getConditionLabels (jsonData, selectedEvent, isMultiShape = false) {
    // For multi-shape paste, preserve all condition labels as-is
    // They will be remapped in the second pass in Workflow.vue
    if (isMultiShape) {
      return jsonData.conditionLabels?.length > 0 ? jsonData.conditionLabels : undefined
    }

    // For single-shape paste, filter by selected event (original behavior)
    if (jsonData.conditionLabels?.length > 0) {
      for (const label of jsonData.conditionLabels) {
        if (label.parentId === selectedEvent.id) {
          return [label]
        }
      }
    }
    return undefined
  },
  getCards (jsonData, svgGraphData, workflow, cardTypes) {
    const keepIDs = !svgGraphData[jsonData.id] && workflow.id === jsonData.workflowId
    let lastSortKey = cardHelpers.getLastCardOnSortKey(workflow)?.sortkey || ''
    return jsonData.cards?.map((card) => {
      // put all the copied cards at the end of the backlog
      // generate a new sortkeys based on the last one
      lastSortKey = cardHelpers.generateNewSortKey(lastSortKey)
      return {
        id: keepIDs ? card.id : randomUUID(),
        description: card.description,
        releaseNo: card.releaseNo || undefined,
        sortkey: lastSortKey,
        cardTypeId: this.getCardTypeId(card, cardTypes, jsonData.cardTypes),
        done: card.done,
        boundedContext: this.getBoundedContext(card, workflow),
        tags: card.tags,
        schemaId: card.schemaId || undefined
      }
    })
  },
  getCardTypeId (card, cardTypes, oldCardTypes) {
    let cardType = cardTypes.find((ct) => ct.id === card.cardTypeId)
    if (cardType) {
      return cardType.id
    }
    const oldCardType = oldCardTypes?.find((ct) => ct.id === card.cardTypeId)
    if (oldCardType) {
      cardType = cardTypes.find((ct) => ct.title === oldCardType.title)
      if (cardType) {
        return cardType.id
      }
    }
    cardType = cardTypes.find((ct) => ct.defaultType === true)
    if (cardType) {
      return cardType.id
    }
    return undefined
  },
  getBoundedContext (card, workflow) {
    if (card.boundedContextId && workflow.boundedContexts.find(bc => bc.id === card.boundedContextId)) {
      return card.boundedContextId
    } else {
      return undefined
    }
  },
  customPasteHandler (event) {
    event.preventDefault()
    let text = (event.clipboardData || window.clipboardData).getData('text')
    text = text.replace(/[\n\r]/g, ' ')
    this.insertTextAtCaret(text)
  },
  insertTextAtCaret (text) {
    const selection = window.getSelection()
    if (!selection.rangeCount) return

    const range = selection.getRangeAt(0)
    range.deleteContents()

    const textNode = document.createTextNode(text)
    range.insertNode(textNode)

    range.setStartAfter(textNode)
    range.setEndAfter(textNode)
    selection.removeAllRanges()
    selection.addRange(range)
  }
}
